import React from 'react';
import { useDispatch } from 'react-redux';
import { Prompt } from 'react-router-dom';
import classnames from 'classnames';
import { sorts, throttle, romanize } from '../../utils';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import Dropzone from 'react-dropzone';
import * as Sentry from '@sentry/browser';
import ProgressBar from '../../components/ProgressBar';
import { updateTrack } from './Audio';
import { addFile, deleteAsset } from './Assets';
import AudioRow from '../../components/title/AudioRow';
import ChapterNamingSidebar from '../../components/title/ChapterNamingSidebar';
import Accordion, { AccordionContents } from '../../components/Accordion';
import Modal from '../../components/Modal';
import { ReactComponent as CaratIcon } from '../../svg/carat-down.svg';
import { ReactComponent as SettingsCollapseIcon } from '../../svg/settings_collapse.svg';
import { ReactComponent as SettingsGear } from '../../svg/gear_white.svg';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { titleUpdate, updateAssets, removeAsset } from '../../slices/titleSlice';

export const AUDIO_STATUS = {
    PENDING: 'pending',
    VALID: 'Valid',
    INVALID: 'Invalid'
};

// using some little inline style helpers to make the app look okay
const getItemStyle = (isDragging, draggableStyle) => {
    // return no style if uploader only handles single files
    return {
        // some basic styles to make the items look a bit nicer
        userSelect: 'none',

        // change background colour if dragging
        border: isDragging ? 'solid blue 2px' : null,

        // styles we need to apply on draggables
        ...draggableStyle
    };
};

const reorder = (list, startIndex, endIndex) => {
    const result = [...list];
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    return result.map((r, i) => {
        return { ...r, groupoffset: i };
    });
};

const sections = {
    front_matter: {
        required: false,
        note: "This section typically begins with the title's opening credits followed by any additional files introducing the listener to the audiobook. This can include the note from the author, dedications, epigraphs, prologues, and any other tracks that introduce your audiobook.",
        groupName: 'Front Matter',
        multiple: true
    },
    body_matter: {
        required: true,
        note: 'This section is the heart of your audiobook. Begin this section with the first chapter of the book. This section can also begin with the beginning of part one or book one depending on the contents of your audiobook. Majority of books will begin with the first chapter and end with the last chapter.',
        groupName: 'Body Matter',
        multiple: true
    },
    back_matter: {
        required: false,
        note: 'This section typically includes all portions of the audiobook that conclude your story. This can range from the epilogue, preview of other audiobooks, conclusion, etc. This section should wrap up with the final track of ending credits. All audiobooks are not required to have ending credits. If your book does not contain any tracks after the final chapter, this section can remain empty.',
        groupName: 'Back Matter',
        multiple: true
    },
    retail_sample: {
        required: true,
        note: "Keep the sample between 1-5 minutes long by showcasing the narration (don't start with music). Avoid explicit language, sexual content, or credits. Listeners will use this sample to preview the audiobook before purchasing, so pick an engaging and dynamic part of the book (without spoilers). Note that some retailers may cut their own samples instead of using this one.",
        groupName: 'Retail Sample',
        multiple: false
    }
};

const AdvancedAudio = ({
    onPlay,
    onPause,
    isPlaying,
    currentTrack,
    className,
    readOnly = false,
    isReordering = false,
    setIsReordering = () => {},
    handleRefetch = async () => {}
}) => {
    const dispatch = useDispatch();
    const { data } = useSelector((state) => state.title);
    const [isDragging, setIsDragging] = React.useState(false);
    const [uploading, setUploading] = React.useState([]);
    const [rightSidebarVisible, setRightSidebarVisible] = React.useState(false);
    const [isHelpModalVisible, setIsHelpModalVisible] = React.useState(false);
    const toastId = React.useRef(null);
    const uploadingRef = React.useRef([]);
    const audioAssetsWithoutGroup = data?.assets.filter(({ assettype, group }) => assettype === 'audio' && !group);

    React.useEffect(() => {
        uploadingRef.current = uploading;
    }, [uploading, uploading.length]);

    React.useEffect(() => {
        window.addEventListener('beforeunload', (e) => {
            if (uploadingRef.current.length > 0) {
                e.returnValue = '';
                return '';
            } else {
                delete e['returnValue'];
                return undefined;
            }
        });
    }, []);

    const getSection = (sectionName) => {
        return data?.assets?.filter((i) => i.group === sectionName) || [];
    };

    const onDragStart = () => {
        setIsDragging(true);
    };

    const onDragEnd = async (result) => {
        const { source, destination, draggableId } = result;
        if (!destination) {
            return;
        }

        const { index: sourceIndex, droppableId: sourceId } = source;
        const { index: destinationIndex, droppableId: destId } = destination;

        setIsDragging(false);
        //draggableId is the track number and we can not update if we do not have an ID
        if (isNaN(draggableId)) {
            return;
        }
        // if there is no change to order, within the same section
        // bail because there is no need to make any API calls.
        if (sourceIndex === destinationIndex && sourceId === destId) {
            return;
        }

        // Downstream side effects have left many titles with chapters with an empty group. This is causing titles to be stuck and unpublishable. We need to reorder the ungrouped section into a pre-set section.
        const isDroppableIdUngrouped = result.source.droppableId === 'ungrouped';

        // Don't allow drag and drop of retail sample section
        if (
            source.droppableId === 'retail_sample' ||
            (destination.droppableId === 'retail_sample' && !isDroppableIdUngrouped)
        ) {
            toast.error('Moving into or out of retail sample is not permitted.');
            return;
        }

        setIsReordering(true);
        try {
            toastId.current = toast('Saving Track Order', { autoClose: false });
            const nonAudioAssets = data.assets.filter((a) => a.assettype !== 'audio');
            const prevSectionedChapters = data.assets
                .filter((chapter) => chapter.assettype !== 'image')
                .reduce((acc, chapter) => {
                    return {
                        ...acc,
                        [chapter.group]: [...(acc[chapter.group] || []), chapter]
                    };
                }, {});

            const end = Number(result.destination.index);

            const dataObj = { groupoffset: end, group: result.destination.droppableId };
            let newAssets = [];
            let newObj = {};

            //if we are moving to a new section we need to include the section in the data object.
            if (
                (result.source.droppableId !== result.destination.droppableId &&
                    Object.keys(sections).includes(result.destination.droppableId)) ||
                isDroppableIdUngrouped
            ) {
                const sourceIndex = result.source.index;
                const destinationIndex = result.destination.index;
                let sourceClone = isDroppableIdUngrouped
                    ? [...audioAssetsWithoutGroup]
                    : [...prevSectionedChapters[result.source.droppableId]];
                let destinationClone = [];

                //if the section is present in prevSectionedChapters set destinationClone value
                if (Object.keys(prevSectionedChapters).includes(result.destination.droppableId)) {
                    destinationClone = [...prevSectionedChapters[result.destination.droppableId]];
                }
                const [removed] = sourceClone.splice(sourceIndex, 1);

                destinationClone.splice(destinationIndex, 0, removed);

                sourceClone = sourceClone.map((s, i) => {
                    return { ...s, group: result.source.droppableId, groupoffset: i };
                });
                destinationClone = destinationClone.map((d, i) => {
                    return { ...d, group: result.destination.droppableId, groupoffset: i };
                });

                newObj = {
                    ...prevSectionedChapters,
                    [result.destination.droppableId]: destinationClone
                };
                // if ungrouped we do not want to include the ungrouped droppableId in the newObj
                if (!isDroppableIdUngrouped) {
                    newObj[result.source.droppableId] = sourceClone;
                }
            } else {
                let items = reorder(
                    prevSectionedChapters[result.source.droppableId] || [],
                    result.source.index,
                    result.destination.index
                );
                items = items.sort((a, b) => sorts.asc(a.groupoffset, b.groupoffset));
                newObj = { ...prevSectionedChapters, [result.source.droppableId]: items };
            }
            Object.keys(sections).forEach((section) => {
                if (!newObj[section]?.length) return;
                newAssets.push(...newObj[section]);
            });
            const remainingUngroupedAssets = audioAssetsWithoutGroup.filter(
                ({ id, assettype }) => assettype === 'audio' && draggableId !== String(id)
            );
            dispatch(
                titleUpdate({
                    assets: [...nonAudioAssets, ...newAssets, ...remainingUngroupedAssets]
                })
            );
            await updateTrack(result.draggableId, dataObj);
            toast.update(toastId.current, {
                type: toast.TYPE.SUCCESS,
                render: 'Track Order Saved',
                autoClose: 3000
            });
        } catch (e) {
            console.error(e);
            toast.error('There was a problem saving your changes. Please refresh and try again.');
            await handleRefetch();
        } finally {
            setIsReordering(false);
        }
    };

    const handleTitleUpdate = (group, title) => {
        let updatedBodyMatter = data.assets.map((t) => {
            if (t.assettype !== 'audio') return t;
            if (t.id === title.id) {
                return title;
            }
            return t;
        });

        dispatch(titleUpdate({ assets: updatedBodyMatter }));
    };

    const handleProgressUpdate = (filename, event) => {
        setUploading((prevUploading) => {
            return prevUploading
                .map((item) => {
                    if (item.filename === filename) {
                        item.progress = event.loaded;
                    }
                    if (item.size === event.loaded) {
                        item.complete = true;
                    }
                    return item;
                })
                .filter((i) => i);
        });
    };

    const handleAudioReplace = async (files = [], group = '', oldFile = null) => {
        let newUploads = files.map((f) => ({ filename: f.name, progress: 0, group, size: f.size }));

        if (files?.length > 1) {
            toast.error('Only 1 retail sample is allowed.');
            return;
        }

        setUploading([...uploading, ...newUploads]);

        try {
            await deleteAsset(oldFile.id);
            dispatch(removeAsset(oldFile));
            let addResp = await addFile(
                data.id,
                files[0],
                {
                    assettype: 'audio',
                    group,
                    groupoffset: 0
                },
                throttle((event) => handleProgressUpdate(files[0].name, event), 500)
            );

            let newState = Object.keys(addResp).map((key) => addResp[key].model);
            dispatch(updateAssets(newState));
            setUploading((prevUploading) => prevUploading.filter((u) => u.filename !== files[0].name));
            toast.success('File replaced successfully');
        } catch (err) {
            toast.error('There was an error uploading one of your files.');
            setUploading([]);
            console.error(err);
            Sentry.captureException(err, {
                tags: {
                    page: 'Advanced Audio'
                },
                contexts: {
                    UploadingFile: {
                        name: files[0].name
                    }
                }
            });
        }
    };

    const handleAudioUpload = async (files = [], group = '', trackCount = 0) => {
        let newUploads = files.map((f) => ({ filename: f.name, progress: 0, group, size: f.size }));
        let noErrors = true;

        if (files.length === 0) {
            toast.error('No files were selected. Please check file type');
            return;
        }

        setUploading([...uploading, ...newUploads]);

        for (let index = 0; index < files.length; index++) {
            let fileData = {
                assettype: 'audio',
                group,
                groupoffset: trackCount + index
            };
            let file = files[index];

            try {
                let addResp = await addFile(
                    data.id,
                    file,
                    fileData,
                    throttle((event) => handleProgressUpdate(file.name, event), 500)
                );

                let newState = Object.keys(addResp).map((key) => addResp[key].model);
                dispatch(updateAssets(newState));
                setUploading((prevUploading) => prevUploading.filter((u) => u.filename !== file.name));
            } catch (err) {
                toast.error('There was an error uploading one of your files.');
                setUploading([]);
                console.error(err);
                Sentry.captureException(err, {
                    tags: {
                        page: 'Advanced Audio'
                    },
                    contexts: {
                        UploadingFile: {
                            name: file.name
                        }
                    }
                });
                noErrors = false;
                break; // Cancel loop since uploading is dependent on file order
            }
        }

        if (noErrors) {
            toast.success('All files uploaded');
        }
    };

    const handleTitleDelete = (id, group) => {
        dispatch(removeAsset({ id }));
    };

    const toggleRightSidebar = () => {
        setRightSidebarVisible(!rightSidebarVisible);
    };

    const applyNamingSettings = (settings) => {
        const separator = settings.separator === 'Space' ? ' ' : settings.separator;
        let renamedAssets = (getSection('body_matter') || []).map((asset, i) => {
            let num = settings.numberType === 'Roman' ? romanize(i + 1) : i + 1;
            let newName = `${settings.prefix} ${num}`;
            if (separator) {
                if (separator === ':') {
                    newName = `${newName}${separator} `;
                } else {
                    newName = `${newName} ${separator} `;
                }
            }
            return { ...asset, name: newName };
        });
        let rest = data.assets.filter((i) => i.group !== 'body_matter');
        toastId.current = toast('Saving Names', { autoClose: false });

        dispatch(titleUpdate({ assets: [...rest, ...renamedAssets] }));

        // Save new names of each track
        const updates = renamedAssets.map((track) =>
            updateTrack(track.id, {
                name: track.name
            })
        );

        Promise.all(updates)
            .then((results) => {
                toast.update(toastId.current, { type: toast.TYPE.SUCCESS, render: 'All Names Saved', autoClose: 3000 });
            })
            .catch((e) => {
                console.error(e);
                toast.update(toastId.current, {
                    type: toast.TYPE.ERROR,
                    render: 'There was a problem saving all updates.',
                    autoClose: 3000
                });
            });
    };

    return (
        <div style={{ overflow: 'auto' }}>
            <Prompt
                when={isReordering}
                message="Files may not save correctly if you leave the page while reorder is saving."
            />
            <Prompt
                when={!!uploadingRef.current.length}
                message="Files may not save correctly if you leave the page while uploading."
            />
            <div className="mb-5">
                <div className="afm-toc-heading">Table of Contents</div>
                <div>
                    Need help?{' '}
                    <button
                        className="btn-link blue"
                        style={{ textDecoration: 'underline' }}
                        onClick={() => setIsHelpModalVisible(!isHelpModalVisible)}
                    >
                        Learn more about Segments
                    </button>
                </div>
            </div>
            <DragDropContext onDragStart={onDragStart} onDragEnd={onDragEnd}>
                {!!audioAssetsWithoutGroup?.length && (
                    // Downstream side effects have left many titles with chapters with an empty group. This is causing titles to be stuck and unpublishable. We need to allow the user to drag and reorder the filtered list of chapters with empty groups into acceptable groups.

                    <div style={{ marginBottom: '50px', border: '1px solid red', padding: '20px' }}>
                        <p>
                            <strong>
                                {`${audioAssetsWithoutGroup.length > 1 ? 'Chapters' : 'Chapter'}`} without segment
                            </strong>
                        </p>
                        <p>These chapters must be sorted into a segment below.</p>
                        <Droppable type="OUTERSECTION" droppableId="ungrouped" isDropDisabled={true}>
                            {(provided, snapshot) => (
                                <div
                                    ref={provided.innerRef}
                                    className={classnames('list-group list-group-outer-drag ui-sortable', {
                                        'drag-over': isDragging
                                    })}
                                >
                                    {(audioAssetsWithoutGroup || []).map((track, i) => (
                                        <Draggable
                                            key={String(track.id)}
                                            draggableId={String(track.id)}
                                            index={i}
                                            isDragDisabled={isReordering}
                                        >
                                            {(provided, snapshot) => (
                                                <div
                                                    ref={provided.innerRef}
                                                    style={getItemStyle(
                                                        snapshot.isDragging,
                                                        provided.draggableProps.style
                                                    )}
                                                    {...provided.draggableProps}
                                                    {...provided.dragHandleProps}
                                                >
                                                    <AudioRow
                                                        id={track.id}
                                                        track={track}
                                                        group="ungrouped"
                                                        onPlay={onPlay}
                                                        onPause={onPause}
                                                        isPlaying={isPlaying}
                                                        currentTrack={currentTrack}
                                                        onTitleUpdate={handleTitleUpdate}
                                                        onTitleDelete={handleTitleDelete}
                                                        readOnly={readOnly || isReordering}
                                                    />
                                                </div>
                                            )}
                                        </Draggable>
                                    ))}

                                    {provided.placeholder}
                                </div>
                            )}
                        </Droppable>
                    </div>
                )}
                {Object.keys(sections).map((key, i) => {
                    let section = sections[key];
                    let tracks = getSection(key) || [];
                    let items = tracks.map((track) => {
                        //below returned ID must be the trackId. we are then parsing it for the trackUpdate.
                        return {
                            id: `${track.id}`,
                            content: (
                                <AudioRow
                                    id={track.id}
                                    track={track}
                                    group={key}
                                    onPlay={onPlay}
                                    onPause={onPause}
                                    isPlaying={isPlaying}
                                    currentTrack={currentTrack}
                                    onTitleUpdate={handleTitleUpdate}
                                    onTitleDelete={handleTitleDelete}
                                    readOnly={track.group === 'retail_sample' || readOnly || isReordering}
                                />
                            )
                        };
                    });

                    let uploads = uploading.filter((u) => u.group === key);

                    return (
                        <Accordion key={`audio_${key}`} className="mb-5" defaultOpen={true}>
                            {({ open, handleItemClick }) => (
                                <>
                                    <div
                                        className="accordion-title at-collapse"
                                        onClick={handleItemClick}
                                        role="button"
                                    >
                                        <h2 className="h5">
                                            {section.groupName}{' '}
                                            {section.required && (
                                                <small>
                                                    <em className="text-danger">(required)</em>
                                                </small>
                                            )}
                                        </h2>
                                        <CaratIcon
                                            width="1em"
                                            height="1em"
                                            stroke="currentColor"
                                            style={{ fontSize: '.75em' }}
                                        />
                                    </div>
                                    <AccordionContents isOpen={open}>
                                        <div className="accordion-body">
                                            <Droppable type="OUTERSECTION" droppableId={key} isDropDisabled={false}>
                                                {(provided, snapshot) => (
                                                    <div
                                                        ref={provided.innerRef}
                                                        className={classnames(
                                                            'list-group list-group-outer-drag ui-sortable',
                                                            {
                                                                'drag-over': isDragging
                                                            }
                                                        )}
                                                    >
                                                        {(items || []).map((item, i) => (
                                                            <Draggable
                                                                key={item.id}
                                                                draggableId={item.id}
                                                                index={i}
                                                                isDragDisabled={isReordering}
                                                            >
                                                                {(provided, snapshot) => (
                                                                    <div
                                                                        ref={provided.innerRef}
                                                                        style={getItemStyle(
                                                                            snapshot.isDragging,
                                                                            provided.draggableProps.style
                                                                        )}
                                                                        {...provided.draggableProps}
                                                                        {...provided.dragHandleProps}
                                                                    >
                                                                        {item.content}
                                                                    </div>
                                                                )}
                                                            </Draggable>
                                                        ))}

                                                        {provided.placeholder}

                                                        {!!uploads.length && (
                                                            <div className="list-group uploads-list">
                                                                {uploads
                                                                    .filter((u) => u.group === key)
                                                                    .map((u, index) => (
                                                                        <div
                                                                            className="list-group-item"
                                                                            key={u.id || `${u.group}_uploads_${index}`}
                                                                        >
                                                                            {u.filename}{' '}
                                                                            <ProgressBar
                                                                                current={u.progress}
                                                                                total={u.size}
                                                                            />
                                                                        </div>
                                                                    ))}
                                                            </div>
                                                        )}

                                                        {!tracks.length && readOnly && (
                                                            <div className="text-default">No tracks in this group.</div>
                                                        )}

                                                        {!section.multiple && !readOnly && !isReordering && (
                                                            <Dropzone
                                                                onDrop={(files) => {
                                                                    if (tracks.length) {
                                                                        handleAudioReplace(files, key, tracks[0]);
                                                                    } else {
                                                                        handleAudioUpload(files, key, tracks.length);
                                                                    }
                                                                }}
                                                                multiple={false}
                                                                accept="audio/flac, audio/mpeg, audio/wav"
                                                            >
                                                                {({ getRootProps, getInputProps }) => (
                                                                    <div
                                                                        className={classnames('btn', className, {
                                                                            hidden: isDragging
                                                                        })}
                                                                        {...getRootProps()}
                                                                    >
                                                                        <input {...getInputProps()} />
                                                                        {tracks.length ? 'Replace Audio' : 'Add Audio'}
                                                                    </div>
                                                                )}
                                                            </Dropzone>
                                                        )}

                                                        {(section.multiple || tracks.length > 1) &&
                                                            !readOnly &&
                                                            !isReordering && (
                                                                <Dropzone
                                                                    onDrop={(files) =>
                                                                        handleAudioUpload(files, key, tracks.length)
                                                                    }
                                                                    multiple={section.multiple}
                                                                    accept="audio/flac, audio/mpeg, audio/wav"
                                                                >
                                                                    {({ getRootProps, getInputProps }) => (
                                                                        <div
                                                                            className={classnames('btn', className, {
                                                                                hidden: isDragging
                                                                            })}
                                                                            {...getRootProps()}
                                                                        >
                                                                            <input {...getInputProps()} />
                                                                            Add Audio
                                                                        </div>
                                                                    )}
                                                                </Dropzone>
                                                            )}
                                                    </div>
                                                )}
                                            </Droppable>
                                        </div>
                                    </AccordionContents>
                                </>
                            )}
                        </Accordion>
                    );
                })}
            </DragDropContext>

            {!rightSidebarVisible && (
                <div className="afm-naming-preferences" onClick={toggleRightSidebar}>
                    <SettingsGear />
                    <span>Chapter Names</span>
                </div>
            )}

            {rightSidebarVisible && (
                <>
                    <div className="afm-collapse-naming-preferences" onClick={toggleRightSidebar}>
                        <SettingsCollapseIcon />
                    </div>
                    <ChapterNamingSidebar applySettings={applyNamingSettings} />
                </>
            )}

            <Modal label="Segment Guidelines" visible={isHelpModalVisible} onClose={() => setIsHelpModalVisible(false)}>
                <div>
                    <h4>Segment Guidelines</h4>
                    <div className="mb-4">
                        <h5>Segment Section Information</h5>
                        {Object.keys(sections).map((key, i) => {
                            let section = sections[key];

                            return (
                                <Accordion key={`guide_audio_${key}`} className="">
                                    {({ open, handleItemClick }) => (
                                        <>
                                            <div
                                                className="accordion-title at-collapse"
                                                onClick={handleItemClick}
                                                role="button"
                                            >
                                                <h2 className="h5">
                                                    {section.groupName}
                                                    {section.required && ' (required)'}
                                                </h2>
                                                <CaratIcon
                                                    width="1em"
                                                    height="1em"
                                                    stroke="currentColor"
                                                    style={{ fontSize: '.75em' }}
                                                />
                                            </div>
                                            <AccordionContents isOpen={open}>
                                                <div className="accordion-body">{section.note}</div>
                                            </AccordionContents>
                                        </>
                                    )}
                                </Accordion>
                            );
                        })}
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default AdvancedAudio;
